import { useEffect, useRef, useState } from 'react';
import apiFetch from 'src/Common/api/auth/Auth';

type SubscriptionPlan = any;

type CacheEntry = {
  data: SubscriptionPlan | null;
  timestamp: number;
  pending?: Promise<SubscriptionPlan> | null;
};

const currentPlanCache: Map<string, CacheEntry> = new Map();
const TTL_MS = 2 * 60 * 1000; // 2 minutes

export function useCurrentPlan(companyId?: string | number) {
  const [data, setData] = useState<SubscriptionPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const idRef = useRef(companyId);

  useEffect(() => {
    idRef.current = companyId;
  }, [companyId]);

  const fetchData = async (force = false) => {
    if (!companyId && companyId !== 0) return;
    const key = String(companyId);
    const now = Date.now();

    const cached = currentPlanCache.get(key);
    const isFresh = cached && now - cached.timestamp < TTL_MS && cached.data && !force;

    if (isFresh) {
      setData(cached!.data);
      setLoading(false);
      setError(null);
      return;
    }

    // De-duplicate in-flight requests
    if (cached?.pending) {
      setLoading(true);
      try {
        const result = await cached.pending;
        if (idRef.current === companyId) {
          setData(result);
          setError(null);
        }
      } catch (err: any) {
        if (idRef.current === companyId) setError(err);
      } finally {
        if (idRef.current === companyId) setLoading(false);
      }
      return;
    }

    setLoading(true);
    const pending = apiFetch(`CompanySubscriptionsPlan/current-plan/${companyId}`);
    currentPlanCache.set(key, { data: cached?.data ?? null, timestamp: now, pending });

    try {
      const result = (await pending) as SubscriptionPlan;
      currentPlanCache.set(key, { data: result, timestamp: Date.now(), pending: null });
      if (idRef.current === companyId) {
        setData(result);
        setError(null);
      }
    } catch (err: any) {
      currentPlanCache.set(key, { data: cached?.data ?? null, timestamp: Date.now(), pending: null });
      if (idRef.current === companyId) setError(err);
    } finally {
      if (idRef.current === companyId) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
  }, [companyId]);

  return {
    currentPlan: data,
    loading,
    error,
    refresh: () => fetchData(true)
  };
}


